import { type AgentStats } from "../../lib/api";
import { formatRelativeTime } from "../../lib/dashboardUtils";

interface AgentPanelProps {
  data?: AgentStats;
  isRunning: boolean;
  onNavigate: (path: string) => void;
}

export function AgentPanel({ data, isRunning, onNavigate }: Readonly<AgentPanelProps>) {
  const lastCrawlStr = formatRelativeTime(data?.runs.lastFinishedAt);
  const lastDeliveryStr = formatRelativeTime(data?.artifacts.lastDeliveredAt);
  const totalRuns = (data?.runs.last24hSucceeded ?? 0) + (data?.runs.last24hFailed ?? 0);
  const failed24h = data?.runs.last24hFailed ?? 0;

  const rows: { label: string; value: React.ReactNode }[] = [];

  if (data?.plantId) {
    rows.push({
      label: "Plant ID",
      value: (
        <span className="block truncate font-mono text-[11.5px] font-semibold text-ink" title={data.plantId}>
          {data.plantId}
        </span>
      ),
    });
  }

  if (data?.agentVersion) {
    rows.push({
      label: "Version",
      value: (
        <span className="block truncate font-mono text-[11.5px]" title={`v${data.agentVersion}`}>
          v{data.agentVersion}
        </span>
      ),
    });
  }

  if (data?.agentId) {
    rows.push({
      label: "Agent ID",
      value: (
        <span className="font-mono text-[11px]" title={data.agentId}>
          {data.agentId.slice(0, 16)}…
        </span>
      ),
    });
  }

  if (data?.startedAt) {
    rows.push({
      label: "Uptime",
      value: formatRelativeTime(data.startedAt) ?? new Date(data.startedAt).toLocaleString(),
    });
  }

  if (isRunning || lastCrawlStr) {
    rows.push({
      label: "Last crawl",
      value: isRunning ? (
        <span className="flex items-center gap-1.5 font-medium text-emerald-700">
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500 animate-pulseSoft" />
          Running now
        </span>
      ) : (
        lastCrawlStr
      ),
    });
  }

  if (totalRuns > 0) {
    rows.push({
      label: "Runs (24h)",
      value: (
        <span className={failed24h > 0 ? "text-amber-700" : "text-emerald-700"}>
          {`${totalRuns - failed24h} succeeded`}
          {failed24h > 0 && (
            <span className="ml-1.5 text-amber-600">{`· ${failed24h} failed`}</span>
          )}
        </span>
      ),
    });
  }

  if (lastDeliveryStr) {
    rows.push({ label: "Last delivery", value: lastDeliveryStr });
  }

  if (data?.artifacts.total != null && data.artifacts.total > 0) {
    rows.push({
      label: "Artifacts",
      value: (
        <span>
          {data.artifacts.delivered} delivered
          <span className="ml-1 text-[10.5px] text-ash">/ {data.artifacts.total} total</span>
        </span>
      ),
    });
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between border-b border-rule px-5 py-3.5">
        <h2 className="text-[14px] font-semibold text-ink">Agent</h2>
        <button
          onClick={() => onNavigate("/settings")}
          className="text-[11.5px] font-medium text-brand-700 hover:underline"
        >
          Settings →
        </button>
      </div>
      {rows.length === 0 ? (
        <div className="px-5 py-6 text-center text-[12.5px] text-ash">Loading agent info…</div>
      ) : (
        <dl className="divide-y divide-rule">
          {rows.map((row) => (
            <div key={String(row.label)} className="grid grid-cols-[96px_1fr] gap-2 px-5 py-2.5">
              <dt className="pt-0.5 text-[10.5px] uppercase tracking-wider text-ash">
                {row.label}
              </dt>
              <dd className="min-w-0 text-[12.5px] text-ink">{row.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
